/**
 * F5 Formulieren — gedeelde DTO's, Zod-schemas en label-maps.
 *
 * Concept: dynamische formulieren-builder. Backoffice (HR/Admin) maakt
 * templates aan met vrije velden; medewerkers leveren inzendingen in.
 *
 * Backoffice-only beheermodule (templates beheren + inzendingen lijst +
 * goed/afkeuren). Inzending-aanmaak is mogelijk vanaf portal of vanuit
 * backoffice; de Zod-schema's zijn shared.
 *
 * Permission-key FORMULIEREN.
 */
import { z } from 'zod'

// ─── Veld-types ─────────────────────────────────────────────────────────────

export const FORMULIER_VELD_TYPES = [
  'text',       // Korte tekst (single line)
  'textarea',   // Lange tekst (multi line)
  'number',     // Getal (kan integer of decimal — geen onderscheid)
  'date',       // ISO yyyy-MM-dd
  'time',       // ISO HH:mm
  'select',     // Single-select uit options
  'multiselect',// Multi-select uit options
  'checkbox',   // Boolean
  'radio',      // Single-select uit options (radio-style)
  'email',      // Email + format-check
] as const
export type FormulierVeldType = (typeof FORMULIER_VELD_TYPES)[number]

export const FORMULIER_VELD_TYPE_LABELS: Record<FormulierVeldType, string> = {
  text: 'Korte tekst',
  textarea: 'Lange tekst',
  number: 'Getal',
  date: 'Datum',
  time: 'Tijd',
  select: 'Keuzelijst',
  multiselect: 'Meervoudige keuze',
  checkbox: 'Aanvinkbaar',
  radio: 'Radio-knoppen',
  email: 'E-mailadres',
}

// ─── Status-enums ───────────────────────────────────────────────────────────

export const FORMULIER_TEMPLATE_STATUS = [
  'CONCEPT',
  'GEPUBLICEERD',
  'GEARCHIVEERD',
] as const
export type FormulierTemplateStatus = (typeof FORMULIER_TEMPLATE_STATUS)[number]

export const FORMULIER_TEMPLATE_STATUS_LABELS: Record<FormulierTemplateStatus, string> = {
  CONCEPT: 'Concept',
  GEPUBLICEERD: 'Gepubliceerd',
  GEARCHIVEERD: 'Gearchiveerd',
}

export const FORMULIER_TEMPLATE_STATUS_BADGE: Record<
  FormulierTemplateStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'
> = {
  CONCEPT: 'warning',
  GEPUBLICEERD: 'success',
  GEARCHIVEERD: 'secondary',
}

export const FORMULIER_INZENDING_STATUS = [
  'INGEDIEND',
  'GOEDGEKEURD',
  'AFGEWEZEN',
] as const
export type FormulierInzendingStatus = (typeof FORMULIER_INZENDING_STATUS)[number]

export const FORMULIER_INZENDING_STATUS_LABELS: Record<FormulierInzendingStatus, string> = {
  INGEDIEND: 'Ingediend',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
}

export const FORMULIER_INZENDING_STATUS_BADGE: Record<
  FormulierInzendingStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'
> = {
  INGEDIEND: 'warning',
  GOEDGEKEURD: 'success',
  AFGEWEZEN: 'destructive',
}

// ─── Veld-definitie schema (shape opgeslagen in template.velden JSON) ───────

const VELD_ID_RE = /^[a-z][a-z0-9_]{0,49}$/

const veldOptieSchema = z.object({
  value: z.string().trim().min(1).max(200),
  label: z.string().trim().min(1).max(200),
})
export type FormulierVeldOptie = z.infer<typeof veldOptieSchema>

/**
 * Definitie van één veld in de template. Volgorde wordt door de
 * template.velden array gedicteerd.
 */
export const formulierVeldSchema = z
  .object({
    /** Stabiele veld-id, gebruikt als key in inzending.data. Lower-snake. */
    id: z.string().regex(VELD_ID_RE, {
      message: 'id moet beginnen met een letter en alleen lower-snake bevatten (max 50)',
    }),
    label: z.string().trim().min(1).max(200),
    type: z.enum(FORMULIER_VELD_TYPES),
    required: z.boolean().default(false),
    helpText: z.string().trim().max(500).optional(),
    placeholder: z.string().trim().max(200).optional(),
    /** Voor select/multiselect/radio. */
    options: z.array(veldOptieSchema).max(50).optional(),
    /** Voor number / text / textarea. */
    min: z.number().optional(),
    max: z.number().optional(),
    minLength: z.number().int().min(0).max(10_000).optional(),
    maxLength: z.number().int().min(1).max(10_000).optional(),
  })
  .superRefine((val, ctx) => {
    const needsOptions =
      val.type === 'select' || val.type === 'multiselect' || val.type === 'radio'
    if (needsOptions) {
      if (!val.options || val.options.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['options'],
          message: `Veld-type "${val.type}" vereist minstens 1 optie`,
        })
      } else {
        const seen = new Set<string>()
        for (const o of val.options) {
          if (seen.has(o.value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['options'],
              message: `Dubbele optie-value: "${o.value}"`,
            })
            break
          }
          seen.add(o.value)
        }
      }
    }
  })
export type FormulierVeldDefinitie = z.infer<typeof formulierVeldSchema>

/**
 * Volledig veld-schema voor een template — een lijst met unieke ids.
 */
export const formulierVeldenSchema = z
  .array(formulierVeldSchema)
  .min(1, 'Minstens 1 veld vereist')
  .max(50, 'Maximaal 50 velden')
  .superRefine((velden, ctx) => {
    const seen = new Set<string>()
    for (let i = 0; i < velden.length; i++) {
      if (seen.has(velden[i].id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, 'id'],
          message: `Dubbele veld-id: "${velden[i].id}"`,
        })
        return
      }
      seen.add(velden[i].id)
    }
  })

// ─── Template Zod-schemas ───────────────────────────────────────────────────

const TEMPLATE_CODE_RE = /^[A-Z0-9][A-Z0-9_-]{0,49}$/

export const createFormulierTemplateSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(TEMPLATE_CODE_RE, 'Code mag alleen hoofdletters, cijfers, _ en - bevatten (max 50)'),
  naam: z.string().trim().min(1).max(120),
  categorie: z.string().trim().min(1).max(80),
  beschrijving: z.string().trim().min(1).max(4000),
  status: z.enum(FORMULIER_TEMPLATE_STATUS).optional(),
  velden: formulierVeldenSchema,
  emailHerinnering: z.boolean().optional(),
})
export type CreateFormulierTemplateInput = z.infer<typeof createFormulierTemplateSchema>

export const updateFormulierTemplateSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(TEMPLATE_CODE_RE, 'Code mag alleen hoofdletters, cijfers, _ en - bevatten (max 50)')
    .optional(),
  naam: z.string().trim().min(1).max(120).optional(),
  categorie: z.string().trim().min(1).max(80).optional(),
  beschrijving: z.string().trim().min(1).max(4000).optional(),
  status: z.enum(FORMULIER_TEMPLATE_STATUS).optional(),
  velden: formulierVeldenSchema.optional(),
  emailHerinnering: z.boolean().optional(),
})
export type UpdateFormulierTemplateInput = z.infer<typeof updateFormulierTemplateSchema>

export const listFormulierTemplateQuerySchema = z.object({
  status: z.enum(FORMULIER_TEMPLATE_STATUS).optional(),
  categorie: z.string().trim().min(1).optional(),
  zoek: z.string().trim().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
})
export type ListFormulierTemplateQuery = z.infer<typeof listFormulierTemplateQuerySchema>

// ─── Inzending Zod-schemas ──────────────────────────────────────────────────

/**
 * Body bij POST /formulieren/inzendingen. Het server-side endpoint valideert
 * `data` opnieuw met `validateFormulierInzending` tegen het template-schema —
 * deze Zod-stap kan dat niet doen (dynamische shape).
 */
export const createFormulierInzendingSchema = z.object({
  templateId: z.string().min(1),
  data: z.record(z.string(), z.unknown()),
  // Ronde 4 W6 — `orderId` verwijderd (Order-model bestaat niet meer). Een
  // inzending koppelt nu via werkbonId of clientId; beide null = losse inzending.
  werkbonId: z.string().min(1).nullable().optional(),
  clientId: z.string().min(1).nullable().optional(),
  emailHerinnering: z.boolean().optional(),
})
export type CreateFormulierInzendingInput = z.infer<typeof createFormulierInzendingSchema>

export const updateFormulierInzendingSchema = z.object({
  data: z.record(z.string(), z.unknown()).optional(),
  emailHerinnering: z.boolean().optional(),
})
export type UpdateFormulierInzendingInput = z.infer<typeof updateFormulierInzendingSchema>

export const beoordeelFormulierInzendingSchema = z.object({
  besluit: z.enum(['GOEDGEKEURD', 'AFGEWEZEN']),
  toelichting: z.string().trim().max(2000).nullable().optional(),
})
export type BeoordeelFormulierInzendingInput = z.infer<typeof beoordeelFormulierInzendingSchema>

export const listFormulierInzendingQuerySchema = z.object({
  templateId: z.string().min(1).optional(),
  status: z.enum(FORMULIER_INZENDING_STATUS).optional(),
  ingediendDoor: z.string().min(1).optional(),
  werkbonId: z.string().min(1).optional(),
  clientId: z.string().min(1).optional(),
  zoek: z.string().trim().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
})
export type ListFormulierInzendingQuery = z.infer<typeof listFormulierInzendingQuerySchema>

// ─── DTO's ──────────────────────────────────────────────────────────────────

export interface FormulierTemplateListRowDTO {
  id: string
  code: string
  naam: string
  categorie: string
  status: FormulierTemplateStatus
  versie: number
  aantalVelden: number
  emailHerinnering: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
  inzendingenAantal: number
}

export interface FormulierTemplateDetailDTO extends FormulierTemplateListRowDTO {
  beschrijving: string
  velden: FormulierVeldDefinitie[]
}

export interface FormulierTemplateStatsDTO {
  totaal: number
  concept: number
  gepubliceerd: number
  gearchiveerd: number
  inzendingenTotaal: number
  inzendingenIngediend: number
}

export interface FormulierInzendingListRowDTO {
  id: string
  templateId: string
  templateCode: string
  templateNaam: string
  templateVersie: number
  ingediendDoor: string
  ingediendDoorNaam: string | null
  status: FormulierInzendingStatus
  beoordeeldDoor: string | null
  beoordeeldDoorNaam: string | null
  beoordeeldOp: string | null
  werkbonId: string | null
  werkbonNumber: string | null
  // Ronde 4 W6 — order-velden verwijderd uit DTO; werkbon-info is leidend.
  clientId: string | null
  clientName: string | null
  emailHerinnering: boolean
  createdAt: string
  updatedAt: string
}

export interface FormulierInzendingDetailDTO extends FormulierInzendingListRowDTO {
  beoordelingsToelichting: string | null
  data: Record<string, unknown>
  /** De template-velden zoals ze waren op het moment van inzenden — UI rendert hier op. */
  templateVelden: FormulierVeldDefinitie[]
  templateBeschrijving: string
}

export interface FormulierInzendingStatsDTO {
  totaal: number
  ingediend: number
  goedgekeurd: number
  afgewezen: number
}
